/**
 * The "go and look at this" button the engine emits under a chat reply
 * (`show_record_link` / `navigate_user`, `src/lib/engine/tools.ts`).
 *
 * Shared because it is rendered on TWO surfaces: the full panel (`MuckaPanel`)
 * and the Mucka Bar's docked transcript (`MobileNav`). It lived in MuckaPanel
 * alone for a while, and the bar's own message contract simply left the field
 * out — so on mobile, where the bar is the default chat surface, the button
 * silently did not exist. One definition, imported by both, is what stops that
 * happening again.
 */

import {
  Briefcase,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  ReceiptPoundSterling,
  Sparkles,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavAction {
  label: string;
  path: string;
  /** When set, the button opens the device's default maps app with directions
   *  to this address instead of navigating within the app. */
  directionsTo?: string;
  /** When set, `path` is a file (an export from `export_records`) and this is
   *  its name: the button saves or shares it instead of navigating. */
  download?: string;
}

/** Pick a leading-chip icon for an engine-emitted nav button based on the
 *  destination path. Matches the look of the "Create" button in entity
 *  modals (Plus chip + label + trail-arrow). */
export function navIconForPath(path: string): LucideIcon {
  if (path.startsWith('/api/exports/')) return FileSpreadsheet;
  if (path.startsWith('/quotes')) return FileText;
  if (path.startsWith('/invoices')) return ReceiptPoundSterling;
  if (path.startsWith('/clients')) return User;
  if (path.startsWith('/jobs')) return Briefcase;
  if (path.startsWith('/settings')) return Sparkles;
  return ExternalLink;
}
